import { Injectable } from '@angular/core';
import { Job, JobApiService } from '@ansys/cloud-angular-client';
import {
  AWFAction,
  ActionContributionsService,
} from '@ansys-internal/awf-angular/contributions';
import { JobMessageData } from '@ansys-internal/awf-angular/sockets';
import { State } from '@ansys-internal/awf-angular/store';
import { lastValueFrom } from 'rxjs';
import { ActionsEnum } from '../actions.enum';
import { JobState } from '../../state/lib/jobs.state';
import { ConceptPopulated } from '../../../api';

@Injectable({
  providedIn: 'root',
})
export class UpdateJobStatus
  implements AWFAction<[string, string], [string, string]>
{
  public readonly type = ActionsEnum.UPDATE_JOB_STATUS;

  constructor(private state: State) {}

  execute: (context: [string, string]) => Promise<[string, string]> = async (
    context: [string, string]
  ) => {
    const jobs: Job[] = await this.state.value(JobState);
    const [jobId, status] = context;
    const job = jobs.find((j) => j.jobId === jobId);

    if (!!job && (job as JobWithExtra)?.['cancelling'] !== true) {
      // temp resolve while we wait for the backend to fix the status/socket desync
      (job as any)['lastStatus'] = status;
      this.state.set(JobState, jobs);
    }

    return [jobId, status];
  };
}

@Injectable({
  providedIn: 'root',
})
export class UpdateJob implements AWFAction<Job, Job> {
  public readonly type = ActionsEnum.UPDATE_JOB;

  constructor(private state: State, private jobApiService: JobApiService) {}

  async execute(context: Job): Promise<Job> {
    const jobs: Job[] = await this.state.value(JobState);
    const updatedJob = context;

    const jobIndex = jobs.findIndex((job) => job.jobId === updatedJob.jobId);
    const currentJob = jobs.find((job) => job.jobId === updatedJob.jobId);
    if (
      (!!currentJob && (currentJob as JobWithExtra)?.['cancelling'] !== true) ||
      updatedJob?.simulations?.[0].lastStatus === 'CANCELLED'
    ) {
      if (jobIndex !== -1) {
        // Update the existing job
        jobs[jobIndex] = updatedJob;
      } else {
        // Add the job if it doesn't exist
        const newJob = await lastValueFrom(
          this.jobApiService.jobLoadHandler({ jobId: updatedJob.jobId })
        );
        jobs.push(newJob || updatedJob);
      }

      this.state.set(JobState, jobs);
    } else {
      return currentJob ?? updatedJob;
    }

    return updatedJob;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AddJob implements AWFAction<string, Job[]> {
  public readonly type = ActionsEnum.ADD_JOB;

  constructor(
    private actions: ActionContributionsService,
    private jobApiService: JobApiService,
    private state: State
  ) {}

  execute: (jobId: string) => Promise<Job[]> = async (jobId: string) => {
    const jobs: Job[] = await this.state.value(JobState);

    const jobExists = jobs.some((job) => job.jobId === jobId);
    if (jobExists) {
      return jobs;
    }

    const newJob = await lastValueFrom(
      this.jobApiService.jobLoadHandler({ jobId })
    );
    jobs.push(newJob);
    return this.actions.execute(SetJobs, jobs);
  };
}

@Injectable({
  providedIn: 'root',
})
export class SetJobs implements AWFAction<Job[], Job[]> {
  public readonly type = ActionsEnum.SET_JOBS;

  constructor(private state: State) {}

  execute: (_jobs?: Job[]) => Promise<Job[]> = async (_jobs?: Job[]) => {
    const currentJobs = this.state.value(JobState) || [];

    if (_jobs) {
      const mergedJobs: Job[] = _jobs.map((incomingJob) => {
        const existingJob = currentJobs.find(
          (j) => j.jobId === incomingJob.jobId
        );

        if ((existingJob as any)?.['cancelling'] === true) {
          return existingJob ?? incomingJob; // fallback in case something went weird
        }

        return incomingJob;
      });

      this.state.set(JobState, mergedJobs);
      return mergedJobs;
    }

    return currentJobs;
  };
}

@Injectable({
  providedIn: 'root',
})
export class UpdateJobProgress
  implements AWFAction<JobMessageData, [string, number]>
{
  public readonly type = ActionsEnum.UPDATE_JOB_PROGRESS;

  constructor(private state: State) {}

  execute: (context: JobMessageData) => Promise<[string, number]> = async (
    context: JobMessageData
  ) => {
    const jobId = context.jobId;
    const currentJob: Job | undefined = this.state
      .value(JobState)
      .find((j) => j.jobId === jobId);

    if (!!currentJob && (currentJob as any)?.['cancelling'] === true) {
      return Promise.resolve([jobId, 0]);
    }

    return [jobId, (context['progress'] as number) * 100];
  };
}

@Injectable({
  providedIn: 'root',
})
export class UpdateJobTimes
  implements AWFAction<JobMessageData, [string, number, number]>
{
  public readonly type = ActionsEnum.UPDATE_JOB_TIMES;

  constructor(private state: State) {}

  execute: (context: JobMessageData) => Promise<[string, number, number]> = async (
    context: JobMessageData
  ) => {
    const jobId = context.jobId;
    const currentJob: Job | undefined = this.state
      .value(JobState)
      .find((j) => j.jobId === jobId);

    if (!!currentJob && (currentJob as any)?.['cancelling'] === true) {
      return Promise.resolve([jobId, 0, 0]);
    }

    const minTime = context['min_time_left'] as number;
    const maxTime = context['max_time_left'] as number;

    return [jobId, minTime, maxTime];
  };
}

@Injectable({
  providedIn: 'root',
})
export class JobMetaData
  implements AWFAction<[string, string], Map<string, string>>
{
  public readonly type = ActionsEnum.SET_JOB_META;
  private jobNames: Map<string, string> = new Map();
  execute: (meta?: [string, string]) => Promise<Map<string, string>> = async (
    meta?: [string, string]
  ) => {
    if (meta) {
      const [jobId, jobName] = meta;
      this.jobNames.set(jobId, jobName);
    }
    return this.jobNames;
  };
}

@Injectable({
  providedIn: 'root',
})
export class LoadAllJobs implements AWFAction<ConceptPopulated, void> {
  public readonly type = ActionsEnum.LOAD_ALL_JOBS;

  constructor(
    private jobApiService: JobApiService,
    private actionService: ActionContributionsService
  ) {}

  execute: (concept: ConceptPopulated) => Promise<void> = async (
    concept: ConceptPopulated
  ) => {
    const jobs = (await Promise.all(
      concept.jobs_ids.map(async (id) => {
        return await lastValueFrom(
          this.jobApiService.jobLoadHandler({ jobId: id })
        ).catch((e) => {
          console.error(e);
        });
      })
    )) as Job[];

    // Clean up undefined jobs (possible problem with a previous delete job - removed from OCM but not ConceptEV
    const jobsClean = jobs.filter((job) => typeof job !== 'undefined') as Job[];
    this.actionService.execute(SetJobs, jobsClean);
  };
}

type LoadJobInputType = string;
type LoadJobReturnType = void;
@Injectable({
  providedIn: 'root',
})
export class LoadJob implements AWFAction<LoadJobInputType, LoadJobReturnType> {
  public readonly type = ActionsEnum.LOAD_JOB;

  constructor(
    private jobApiService: JobApiService,
    private actionService: ActionContributionsService,
    private state: State
  ) {}

  execute: (context: LoadJobInputType) => Promise<LoadJobReturnType> = async (
    jobId: LoadJobInputType
  ) => {
    const job = await lastValueFrom(
      this.jobApiService.jobLoadHandler({ jobId: jobId })
    ).catch((e) => {
      console.error(e);
    });

    if (!job) {
      return;
    }

    const jobs = this.state.value(JobState) || [];
    const jobsClean = jobs.filter((job) => job !== undefined) as Job[];
    const jobIndex = jobsClean.findIndex((j) => j.jobId === jobId);

    if (jobIndex !== -1 && job) {
      jobsClean[jobIndex] = job;
    } else if (job) {
      // jobsClean.push(job);
    }

    this.actionService.execute(SetJobs, jobsClean);
  };
}

export interface JobWithExtra extends Job {
  cancelling?: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class JobCancelProcessing implements AWFAction<string, JobWithExtra[]> {
  public readonly type = ActionsEnum.JOB_CANCEL_PROCESSING;

  constructor(private state: State) {}

  execute: (jobId: string) => Promise<JobWithExtra[]> = async (
    jobId: string
  ) => {
    const jobs: Job[] = this.state.value(JobState) as JobWithExtra[];
    const job = jobs.find((j) => j.jobId === jobId);
    if (job) {
      (job as any)['cancelling'] = true;
      this.state.set(JobState, jobs);
    }

    return jobs;
  };
}
